import { WSQueryCache } from "@ws-react-query";
import { QUERY_PAYEE } from "../../../query/payee/keys";
import {
  QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID,
  QUERY_PAYEE_ENGAGEMENTS_LIST,
  QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL,
  QUERY_PAYEE_ENGAGEMENTS_LIST_SIZE
} from "../../../query/payeeEngagements/keys";
import { QUERY_PAYEE_ROWS } from "../../../query/search/payee/queries/useQueryPayeeRows";
import { QUERY_PAYEE_ROWS_SUMMARY } from "../../../query/search/payee/queries/useQueryPayeeRowsSummary";
import { createPayeeEngagementByPayeeId } from "../../../services/payeeEngagement/createPayeeEngagementByPayeeId";
import {
  bulkActionFactory,
  BulkActionProgress,
  useForegroundBulkAction
} from "../../BulkActions";
import { useXWingspanPayerId } from "../routes/RoutePayeeDetails/useXWingspanPayerId";

export interface AssignEngagementBulkItem {
  payeeId: string;
  engagementId: string;
  name?: string;
}

export const useAssignEngagementsBulkAction = (): ((
  items: AssignEngagementBulkItem[]
) => Promise<boolean>) => {
  const runBulkAction = useForegroundBulkAction();
  const xWingspanPayerId = useXWingspanPayerId();

  return async items => {
    let didRun = false;
    await runBulkAction({
      actionName: "Add assignments",
      itemCount: items.length,
      skipConfirm: true,
      prepare: async () => items,
      process: async (data, onProgress): Promise<BulkActionProgress> => {
        didRun = true;
        const bulk = bulkActionFactory<AssignEngagementBulkItem>({
          action: async item =>
            createPayeeEngagementByPayeeId(
              item.payeeId,
              { engagementId: item.engagementId, payerOwnedData: {} },
              xWingspanPayerId
            ),
          getSuccessDescription: item =>
            item.name ? `Assigned "${item.name}"` : "",
          getProgressMessage: (item, progress) =>
            `Assigning engagement ${Math.min(
              progress.processed + 1,
              progress.total
            )} of ${progress.total}...`,
          getFailedDescription: item =>
            item.name ? `Assign "${item.name}"` : "Assign engagement",
          onProgress
        });

        const { failedItems, successItems } = await bulk.run(data);

        WSQueryCache.invalidateQueries(QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID);
        WSQueryCache.invalidateQueries(QUERY_PAYEE_ENGAGEMENTS_LIST);
        WSQueryCache.invalidateQueries(
          QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL
        );
        WSQueryCache.invalidateQueries(QUERY_PAYEE_ENGAGEMENTS_LIST_SIZE);
        WSQueryCache.invalidateQueries(QUERY_PAYEE);
        WSQueryCache.invalidateQueries(QUERY_PAYEE_ROWS);
        WSQueryCache.invalidateQueries(QUERY_PAYEE_ROWS_SUMMARY);

        return {
          failed: failedItems.length,
          failedDescriptions: failedItems.map(item =>
            item.name ? `Assign "${item.name}"` : "Assign engagement"
          ),
          processed: successItems.length + failedItems.length,
          successful: successItems.length,
          successDescriptions: successItems
            .filter(item => item.name)
            .map(item => `Assigned "${item.name}"`),
          total: data.length
        };
      }
    });
    return didRun;
  };
};
